// outbox-identity.js — whose change is this?
// ---------------------------------------------------------------------
// The outbox lives in IndexedDB, which is scoped to the BROWSER PROFILE,
// not to a sign-in. Nothing in a queued row said who created it, so a
// change queued under one session drained under whatever session happened
// to be signed in later. On a shared site tablet — or after an InSpec
// client-portal hand-off drops a different person into the same browser —
// that means one person's edit lands under another person's name.
//
// That is not theoretical here: v0.113.0 fixed a real cross-identity bug
// where a client ended up inside an internal session. Same browser, two
// identities, minutes apart. Any row queued across that boundary would
// have replayed under the wrong one.
//
// So every queued row now carries the identity that created it, and the
// drain refuses to send a row whose stamp does not match the identity
// signed in right now.
//
// NO NEW IDENTITY CONCEPT IS INVENTED. The stamp is derived from the two
// session stores DropPin already has:
//   • droppin.user.v1     — InSite-ID staff/owner session   (user.js)
//   • droppin.contact.v1  — subbie / client contact session (contact-session.js)
//
// NOTHING SENSITIVE IS STORED. The stamp is ids only — no token, no JWT,
// no email, no name. The real credential stays where it always was: in an
// httpOnly cookie this code cannot read. The stamp is a comparison key,
// not a credential, and it is never used to authorise anything — only to
// refuse.
//
// Both sessions are folded into ONE key rather than picking a winner.
// An admin can also be invited as a contact, so both stores can be
// populated at once; combining them means ANY change of signed-in
// identity produces a different key. Refusing too often is recoverable
// (the user is shown the change and can act on it); applying a write
// under the wrong identity is not.

import { getCurrentUser } from './user.js';
import { getContactSession } from './contact-session.js';

// ── Key derivation (pure — unit-tested in node) ───────────────────────
//
// identityKeyFrom(user, contactSession) → 'u:42|c:7:19' | null
//
// null means "no identifiable session". A null key is never treated as
// matching anything, including another null.
export function identityKeyFrom(user, contactSession) {
  const userId = firstDefined(user?.id, user?.Id);
  const contact = contactSession?.contact || null;
  const contactId = firstDefined(contact?.id, contact?.Id);
  const tenantId = firstDefined(contact?.tenantId, contact?.TenantId);

  const uPart = userId != null && userId !== '' ? `u:${userId}` : 'u:';
  const cPart = contactId != null && contactId !== ''
    ? `c:${tenantId != null && tenantId !== '' ? tenantId : '?'}:${contactId}`
    : 'c:';

  // A contact session that exists but carries no usable contact id is
  // NOT identifiable. Falling back to a shared placeholder would make
  // two different anonymous contacts compare equal — the exact failure
  // this module exists to prevent.
  if (contactSession && contactId == null) return null;
  if (uPart === 'u:' && cPart === 'c:') return null;
  return `${uPart}|${cPart}`;
}

function firstDefined(...vals) {
  for (const v of vals) if (v != null && v !== '') return v;
  return null;
}

// The identity signed in right now, read from the live session stores.
export function getCurrentIdentityKey() {
  try {
    return identityKeyFrom(getCurrentUser(), getContactSession());
  } catch (_e) {
    return null;   // unreadable storage → unidentifiable → fail closed
  }
}

// ── Verdicts (pure — unit-tested in node) ─────────────────────────────
//
// classifyQueuedItem(item, currentKey) → one of:
//
//   'match'      stamped, and the stamp equals the identity signed in
//                now. The ONLY verdict that lets a row be sent.
//   'mismatch'   stamped with a DIFFERENT identity. Never sent. The
//                user is told plainly and can discard.
//   'unstamped'  queued before DropPin recorded who made a change (or
//                by a session it could not identify). Never sent
//                automatically — we cannot prove it is the current
//                user's, and guessing "probably theirs" is precisely
//                how a write lands under the wrong name. Shown to the
//                user, who can send or discard it deliberately.
//   'no-session' nobody is identifiably signed in. Nothing is sent;
//                the API would reject it anyway, and a row consumed
//                against a 401 would be lost for good.
export function classifyQueuedItem(item, currentKey) {
  const stamped = item && typeof item.identityKey === 'string' && item.identityKey
    ? item.identityKey
    : null;
  if (!currentKey) return 'no-session';
  if (!stamped)    return 'unstamped';
  return stamped === currentKey ? 'match' : 'mismatch';
}

// True when a verdict means "do not send this row".
export function isBlocked(verdict) {
  return verdict !== 'match';
}
